import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const canReadAdminDoc = async (collectionName, docId) => {
  try {
    const snapshot = await getDoc(doc(db, collectionName, docId));
    return snapshot.exists();
  } catch (error) {
    if (error?.code === 'permission-denied') {
      return false;
    }
    throw error;
  }
};

const hasAdminAccess = async (uid) => {
  const directEntries = await Promise.all([
    canReadAdminDoc('admins', uid),
    canReadAdminDoc('admin', uid),
  ]);
  if (directEntries.some(Boolean)) return true;

  const legacyEntries = await Promise.all([
    canReadAdminDoc('admins', 'Main'),
    canReadAdminDoc('admins', 'main'),
    canReadAdminDoc('admin', 'Main'),
    canReadAdminDoc('admin', 'main'),
  ]);
  return legacyEntries.some(Boolean);
};

export function useAdminAuth() {
  const [user, setUser] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (nextUser) => {
      if (!nextUser) {
        if (cancelled) return;
        setUser(null);
        setAuthUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      if (!cancelled) {
        setAuthUser(nextUser);
        setLoading(true);
        setIsAuthenticated(true);
      }

      try {
        const allowed = await hasAdminAccess(nextUser.uid);
        if (cancelled) return;
        setIsAdmin(allowed);
        setUser(allowed ? nextUser : null);
      } catch (error) {
        if (cancelled) return;
        console.error('Admin access check failed:', error);
        setIsAdmin(false);
        setUser(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  return { user, authUser, loading, isAuthenticated, isAdmin };
}
